import { useCallback, useEffect, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react'
import { ActionType, type Chart } from 'klinecharts'
import { cn } from '@/lib/utils'
import { clampGap, MAX_RIGHT_GAP_PX, MIN_RIGHT_GAP_PX } from './chartGapStore'

const KEY_STEP_PX = 8
/** Room kept for candles on a narrow chart, so the gap never swallows them. */
const KEEP_CANDLES_PX = 120

// DomPosition is a const enum in klinecharts' d.ts; cast the literal once (as klineViewport does).
type DomPositionArg = Parameters<Chart['getDom']>[1]
const DOM_MAIN = 'main' as DomPositionArg

export interface ChartGapHandleProps {
  chart: Chart | null
  /** The gap the user chose, px; null reads the chart's own. */
  gap: number | null
  /** The user moved the gap, px: store it. */
  onChange: (gapPx: number) => void
  /** The gap this chart shows now, fitted to its width, px: apply it to the chart. Keep it stable. */
  onApply: (gapPx: number) => void
  /** Re-measure when this changes, for layout moves the engine does not report. */
  revision?: unknown
  className?: string
}

/** The candle pane's right edge (where the price axis starts) and width, in the host's pixels. */
function paneOf(chart: Chart, host: HTMLElement): { right: number; width: number } | null {
  const pane = chart.getDom('candle_pane', DOM_MAIN)?.getBoundingClientRect()
  if (!pane) return null
  return { right: pane.right - host.getBoundingClientRect().left, width: pane.width }
}

/**
 * A chevron on the chart's top edge, at the end of the gap after the last candle: drag it to set
 * that gap. Render it inside the chart's positioned container.
 */
export function ChartGapHandle({ chart, gap, onChange, onApply, revision, className }: ChartGapHandleProps) {
  const rootRef = useRef<HTMLDivElement>(null)
  const [pane, setPane] = useState<{ right: number; width: number } | null>(null)
  const [dragging, setDragging] = useState(false)
  const dragRef = useRef<{ x: number; gap: number } | null>(null)
  const frameRef = useRef(0)

  const remeasure = useCallback(() => {
    cancelAnimationFrame(frameRef.current)
    frameRef.current = requestAnimationFrame(() => {
      const host = rootRef.current?.parentElement
      const next = chart && host ? paneOf(chart, host) : null
      setPane((prev) => (prev && next && prev.right === next.right && prev.width === next.width ? prev : next))
    })
  }, [chart])

  useEffect(() => {
    if (!chart) return undefined
    const host = rootRef.current?.parentElement
    const observer = host ? new ResizeObserver(remeasure) : null
    if (host) observer?.observe(host)
    // The price axis widens and narrows with the prices on screen.
    chart.subscribeAction(ActionType.OnVisibleRangeChange, remeasure)
    remeasure()
    return () => {
      cancelAnimationFrame(frameRef.current)
      observer?.disconnect()
      chart.unsubscribeAction(ActionType.OnVisibleRangeChange, remeasure)
    }
  }, [chart, remeasure])

  useEffect(() => remeasure(), [remeasure, revision])

  const limit = pane ? Math.max(MIN_RIGHT_GAP_PX, Math.min(MAX_RIGHT_GAP_PX, pane.width - KEEP_CANDLES_PX)) : null
  const fitted = gap != null && limit != null ? Math.min(gap, limit) : null
  useEffect(() => {
    if (chart && fitted != null) onApply(fitted)
  }, [chart, fitted, onApply])

  if (!chart || !pane || limit == null) return <div ref={rootRef} className="hidden" />
  const current = fitted ?? chart.getOffsetRightDistance()
  const x = pane.right - current
  const move = (px: number) => {
    const next = Math.min(clampGap(px), limit)
    onApply(next)
    onChange(next)
  }

  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return
    // The chart's own pan and click handlers sit on the same container.
    e.stopPropagation()
    e.preventDefault()
    e.currentTarget.setPointerCapture(e.pointerId)
    dragRef.current = { x: e.clientX, gap: current }
    setDragging(true)
  }
  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    const d = dragRef.current
    if (!d) return
    e.stopPropagation()
    move(d.gap - (e.clientX - d.x))
  }
  const onPointerUp = (e: PointerEvent<HTMLDivElement>) => {
    if (!dragRef.current) return
    e.stopPropagation()
    dragRef.current = null
    setDragging(false)
  }
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return
    e.preventDefault()
    move(current + (e.key === 'ArrowLeft' ? KEY_STEP_PX : -KEY_STEP_PX))
  }

  return (
    <div ref={rootRef} className={cn('pointer-events-none absolute inset-0 z-[5]', className)}>
      {dragging && (
        <div
          aria-hidden="true"
          data-testid="chart-gap-line"
          className="absolute top-0 bottom-0 w-0 border-l border-dashed border-muted-foreground/40"
          style={{ left: x }}
        />
      )}
      <div
        role="slider"
        tabIndex={0}
        title="Drag to set the gap after the last candle"
        aria-label="Space after the last candle"
        aria-orientation="horizontal"
        aria-valuemin={MIN_RIGHT_GAP_PX}
        aria-valuemax={limit}
        aria-valuenow={Math.round(current)}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onClick={(e) => e.stopPropagation()}
        onDoubleClick={(e) => e.stopPropagation()}
        onKeyDown={onKeyDown}
        className={cn(
          'group pointer-events-auto absolute top-0 flex h-5 w-5 -translate-x-1/2 touch-none items-start justify-center outline-none',
          dragging
            ? 'cursor-grabbing text-muted-foreground/70'
            : 'cursor-grab text-muted-foreground/50 hover:text-muted-foreground/70 focus-visible:text-muted-foreground/70',
        )}
        style={{ left: x }}
      >
        <svg
          viewBox="0 0 12 8"
          preserveAspectRatio="none"
          aria-hidden="true"
          className={cn('h-1.25 w-2 group-hover:h-2 group-hover:w-3', dragging && 'h-2 w-3')}
        >
          <path d="M0 0H12L6 8Z" fill="currentColor" />
        </svg>
      </div>
    </div>
  )
}
